import RpcProxyClient from './RpcProxyClient'
import { getAccessToken } from '@/libs/Auth'
import TicketUpdate from '@/utils/tickets/TicketUpdate'
import { envToBool } from '@/helpers'

const debug = envToBool(
  'VUE_APP_RPC_DEBUG',
  process.env.NODE_ENV !== 'production'
)

interface User {
  uuid: string;
  name: string;
  lastName: string;
  firstName: string;
  token: string;
  userid: string;
  location: {
    uuid: string;
    name: string;
  };
  email?: string;
  organization: {
    uuid: string;
    name: string;
  };
  roles?: [];
  groups: [
    {
      uuid: string;
      name: string;
    },
    {
      uuid: string;
      name: string;
    }
  ];
  accessType: string;
  default_group?: string;
  companyName?: string;
}

export class Analyst {
  public uuid?: string;
  public name: string;
  public email?: string;
  public company?: string;
  public organization?: string;

  constructor (user: User) {
    this.name = user.name
    this.email = user.email
    switch (user.accessType) {
      case 'PARTNER_ACCESS':
        this.uuid = user.default_group || user.uuid
        this.company = user.companyName
        break
      case 'CLIENT_ACCESS':
        this.company = user.companyName
        break
      default:
        this.uuid = user.uuid
        break
    }
    this.organization = user.organization.name
  }

  toString (): string {
    const values = [this.name]
    if (this.company) {
      values.push(`(${this.company})`)
    } else {
      if (this.organization) {
        values.push(`(${this.organization})`)
      }
    }
    return values.join(' ')
  }

  values () {
    return JSON.parse(JSON.stringify(this))
  }
}

export interface Preset {
  filter_model: Array<any>;
  default: boolean;
  userid: string;
  spaid: string;
  column_model: Array<any>;
  preset_name: string;
  type: string;
}

export default class TicketsService {
  private static _client: RpcProxyClient | null = null;
  private static _ready: Promise<void> | null = null;

  private static get client (): RpcProxyClient {
    if (!this._client) {
      const base = process.env.VUE_APP_PORTAL_API_BASE
      const stage = process.env.VUE_APP_PORTAL_API_STAGE
      const baseUrl = `${base}/${stage}`
      if (!baseUrl) throw new Error('Invalid URL for Portal API')

      this._client = new RpcProxyClient({
        baseUrl,
        getToken: async () =>
          (await getAccessToken(
            {
              scopes: [
                'api://5deddb7b-2801-42a0-a6f5-129b6bccbbcd/tickets-service'
              ]
            },
            true
          )) ?? '',
        debug
      })
    }
    return this._client
  }

  private static async ready () {
    if (!this._ready) {
      this._ready = (async () => {
        try {
          await Promise.resolve(this.client)
        } catch (e) {
          this._ready = null
          throw e
        }
      })()
    }
    return this._ready
  }

  static reset (): void {
    this._client = null
  }

  public static async getTicketsByOrga (
    query: object = {},
    projection: object = {},
    options: object = {}
  ) {
    // eslint-disable-next-line no-console
    console.log(
      `[TicketsService] getTicketsByOrga:\n${JSON.stringify(
        { query, projection, options },
        null,
        2
      )}`
    )
    await this.ready()
    const data = await this.client.fromStream.getTicketsByOrga(
      query,
      projection,
      options
    )
    return data.result
  }

  public static async getTickets (
    scopes: string[] = [],
    options: object = {},
    stream: any = null
  ) {
    // eslint-disable-next-line no-console
    console.log(
      `[RealTime][TicketsService] getTickets:\n${JSON.stringify(
        { scopes, options, stream },
        null,
        2
      )}`
    )
    await this.ready()

    const onItem = stream && typeof stream.onItem === 'function' ? stream.onItem : null
    const onDone = stream && typeof stream.onDone === 'function' ? stream.onDone : null
    const collect = !(stream && stream.collect === false)

    if (onItem) {
      const data = await this.client.fromStream.getTickets(
        scopes,
        options,
        {
          stream: true,
          collect,
          onItem,
          onDone
        }
      )

      if (!collect) {
        return []
      }

      if (!onItem || collect) {
        // eslint-disable-next-line no-console
        console.log('[RealTime][TicketsService] getTickets data:', data)
      }
      return data.result
    }

    const data = await this.client.fromStream.getTickets(
      scopes,
      options
    )

    // eslint-disable-next-line no-console
    console.log('[RealTime][TicketsService] getTickets data:', data)
    return data.result
  }

  public static async getTicket (id: string) {
    // eslint-disable-next-line no-console
    console.log(`[RealTime][TicketsService] getTicket (id): ${id}`)
    await this.ready()
    const data = await this.client.functions.getTicket(id)
    // eslint-disable-next-line no-console
    console.log('[RealTime][TicketsService] getTicket data:', data)
    return data
  }

  public static async getActivityLogs (
    query: object = {},
    projection: object = {},
    options: object = {}
  ) {
    await this.ready()
    const data = await this.client.fromStream.getActivityLogs(
      query,
      projection,
      options
    )
    return data.result
  }

  public static async getContact (payload) {
    // eslint-disable-next-line no-console
    console.log('[TicketsService] getContact:', payload)
    await this.ready()
    const data = await this.client.functions.getContact(payload)
    // eslint-disable-next-line no-console
    console.log('[TicketsService] getContact data:', data)
    return data
  }

  public static async getViewAsList () {
    // eslint-disable-next-line no-console
    console.log('[TicketsService] getViewAsList')
    await this.ready()
    const data = await this.client.functions.getViewAsList()
    // eslint-disable-next-line no-console
    console.log('[TicketsService] getViewAsList data:', data)
    return data
  }

  public static async getContacts () {
    await this.ready()
    const data = await this.client.functions.getContacts()
    return data.result
  }

  public static async getContactsGroup (organizationUUID: string) {
    await this.ready()
    const data = await this.client.functions.getContactsGroup(organizationUUID)
    return data.result
  }

  public static async getPresets (userid: string) {
    await this.ready()
    const data = await this.client.functions.getPreset(userid)
    return data.result
  }

  public static async createPreset (preset: Preset) {
    await this.ready()
    const data = await this.client.functions.createPreset(preset)
    return data.result
  }

  public static async deletePreset (preset: Preset) {
    await this.ready()
    const data = await this.client.functions.deletePreset(preset)
    return data.result
  }

  public static async updatePreset (preset: Preset) {
    await this.ready()
    const data = await this.client.functions.updatePreset(preset)
    return data.result
  }

  public static async getRowdata () {
    await this.ready()
    const data = await this.client.fromStream.getRowdata()
    return data.result
  }

  public static async sendTicketUpdate (
    user: User,
    ticketUpdate: TicketUpdate,
    description: string
  ) {
    // eslint-disable-next-line no-console
    console.log('[TicketsService][TicketsUpdate] sendTicketUpdate args', { user, ticketUpdate, description })
    await this.ready()
    // eslint-disable-next-line no-console
    console.log('[TicketsService][TicketsUpdate] client ready!')
    const analyst = new Analyst(user)

    const body = {
      description: description,
      analyst: analyst.values(),
      tech_info: ticketUpdate.getObject(false),
      log_type: ticketUpdate.updateType,
      ref_id: ticketUpdate.ticket.referenceId,
      ref_group: ticketUpdate.ticket?.group?.id,
      platform: 'SDM',
      source: {
        app_id: process.env.VUE_APP_SPA_ID,
        app_version: process.env.VUE_APP_SPA_VERSION
      },
      status: 'CREATED'
    }
    if (!body.analyst.uuid) {
      body.analyst.uuid = ticketUpdate.ticket?.group?.id
    }

    // eslint-disable-next-line no-console
    console.log('[TicketsService][TicketsUpdate] sendTicketUpdate', body)
    const response = await this.client.functions.createUpdateTicket(body)
    // eslint-disable-next-line no-console
    console.log('[TicketsService][TicketsUpdate] sendTicketUpdate response', response)

    return response
  }

  public static async publishTicketUpdate (id: string) {
    await this.ready()
    const body = {
      id: id
    }

    // eslint-disable-next-line no-console
    console.log('[TicketsService][TicketsUpdate] publishTicketUpdate', body)
    const response = await this.client.functions.publishTicketUpdate(body)
    // eslint-disable-next-line no-console
    console.log('[TicketsService][TicketsUpdate] publishTicketUpdate response', response)

    return response
  }
}
